import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { useEffect, useRef } from 'react';

export interface UserProfile {
  display_name: string | null;
  avatar_url: string | null;
}

/**
 * Extract best-effort display_name and avatar_url from auth user metadata.
 * Google provides: full_name, name, avatar_url, picture.
 */
function extractSeedData(meta: Record<string, unknown> | undefined): {
  display_name: string | null;
  avatar_url: string | null;
} {
  if (!meta) return { display_name: null, avatar_url: null };

  const name = (meta.full_name || meta.name || meta.user_name) as string | undefined;
  const avatar = (meta.avatar_url || meta.picture) as string | undefined;

  return {
    display_name: name?.trim() || null,
    avatar_url: avatar?.trim() || null,
  };
}

export function useMyProfile() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const seededRef = useRef(false);

  const query = useQuery({
    queryKey: ['my-profile', user?.id],
    queryFn: async (): Promise<UserProfile | null> => {
      const { data, error } = await supabase.rpc('get_my_profile');
      if (error) throw error;
      if (!data || (data as unknown[]).length === 0) return null;
      const row = (data as unknown as UserProfile[])[0];
      return row;
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
  });

  // Auto-seed from Google metadata on first load (seed-once, never overwrite)
  useEffect(() => {
    if (seededRef.current || !user?.id || query.isLoading) return;
    if (query.data !== null && query.data !== undefined) {
      // Profile exists – check if fields need seeding
      if (query.data.display_name && query.data.avatar_url) {
        seededRef.current = true;
        return;
      }
    }

    const seed = extractSeedData(user.user_metadata);
    if (!seed.display_name && !seed.avatar_url) {
      seededRef.current = true;
      return;
    }

    seededRef.current = true;
    supabase
      .rpc('seed_my_profile', {
        p_display_name: seed.display_name,
        p_avatar_url: seed.avatar_url,
      })
      .then(() => {
        queryClient.invalidateQueries({ queryKey: ['my-profile', user.id] });
      });
  }, [user?.id, query.isLoading, query.data]);

  return {
    profile: query.data ?? null,
    isLoading: query.isLoading,
    error: query.error,
  };
}

export function useUpsertMyProfile() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (params: { display_name?: string | null; avatar_url?: string | null }) => {
      const { data, error } = await supabase.rpc('upsert_my_profile', {
        p_display_name: params.display_name ?? null,
        p_avatar_url: params.avatar_url ?? null,
      });
      if (error) throw error;
      return (data as unknown as UserProfile[])[0];
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-profile', user?.id] });
    },
  });
}

export function useClearMyProfileField() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (field: 'display_name' | 'avatar_url') => {
      const { error } = await supabase.rpc('clear_my_profile_field', {
        p_field: field,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-profile', user?.id] });
    },
  });
}
